import { Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { type Location, useLocation } from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import { Header } from 'components/navigation/Header'
import { StatusCheckBreadcrumbs } from 'components/status-checks/common/StatusCheckBreadcrumbs'
import { getFormattedStatusCheck } from 'components/status-checks/create/create-status-check-utils'
import { CreateStatusCheckForm } from 'components/status-checks/create/CreateStatusCheckForm'
import { EditStatusCheckFormContainer } from 'components/status-checks/edit/EditStatusCheckFormContainer'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useGetServiceById } from 'queries/services/use-get-service-by-id'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { UserDefinedService } from 'types/api/services/service'
import type {
  ServiceStatusCheckFromStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'

/*
 * The statusCheck passed into location can be of either type ServiceStatusCheckFromStatusCheck or StatusCheck
 * TODO: EN-6581
 */
type LocationState = {
  referrer?: string
  service?: UserDefinedService
  statusCheck?: StatusCheck | ServiceStatusCheckFromStatusCheck
} | null

export const CREATE_STATUS_CHECK_HEADER_DESCRIPTION =
  "First, authenticate to your observability tool and then tell Gremlin which monitor or alert will help us automatically assess if your Service or Scenario is failing. If you don't have your monitors set up yet, we recommend setting up monitors that look at: latency, traffic, error rate, and resource saturation."

export const CreateStatusCheck: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const location: Location<LocationState> = useLocation()

  const {
    referrer = statusChecksUrls.base(),
    service: serviceFromLocationState,
    statusCheck
  } = location.state ?? {}
  const { serviceId } = serviceFromLocationState ?? {}

  /*
   * Data coming from location.state may be stale, so we re-fetch the service record if we have a serviceId.
   * This becomes important to ensure that newly created status checks properly append to all existing ones.
   * Otherwise, we have observed that the new status check associations wipe the previous assignments.
   */
  const { data: service } = useGetServiceById(serviceId, {
    enabled: Boolean(serviceId),
    placeholderData: serviceFromLocationState
  })

  const {
    error: statusCheckError,
    isLoading: isLoadingStatusChecks,
    isError: hasErrorStatusChecks
  } = useGetStatusChecksAsServiceStatusChecks({ enabled: false })

  const {
    awsIAMRoleMetadataError,
    hasErrorAwsIAMRoleMetadata,
    isCreatingAuthenticationOnly,
    setIsCreatingAuthenticationOnly,
    isFetchingAwsIAMRoleMetadata,
    isEdit,
    setIsEdit,
    isOnboarding,
    setIsOnboarding
  } = useObservabilityToolAuthenticationContext()

  const isLoading = isLoadingStatusChecks || isFetchingAwsIAMRoleMetadata
  const isError = hasErrorStatusChecks || hasErrorAwsIAMRoleMetadata

  /* If this is defined, we know we have cloned an existing statusCheck */
  const formattedStatusCheckClone = useMemo(() => {
    return statusCheck ? getFormattedStatusCheck(statusCheck) : undefined
  }, [statusCheck])

  const pageTitle = formattedStatusCheckClone
    ? 'Clone Health Check'
    : 'Create Health Check'

  const headerText = formattedStatusCheckClone
    ? `Clone Health Check: ${formattedStatusCheckClone.name}`
    : 'Create a new Health Check'

  useEffect(() => {
    if (!isCreatingAuthenticationOnly) return

    setIsCreatingAuthenticationOnly?.(false)
  }, [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly])

  useEffect(() => {
    if (!isEdit) return

    setIsEdit?.(false)
  }, [isEdit, setIsEdit])

  /**
   * The isOnboarding flag is stored in the ObservabilityToolAuthenticationContext's state and is used to make rendering
   * decisions. Since this context is used in onboarding and non-onboarding workflows, we manually set the flag prior to
   * continuing.
   *
   * This workflow requires the isOnboarding flag value to be false.
   */
  useEffect(() => {
    if (!isOnboarding) return

    setIsOnboarding?.(false)
  }, [isOnboarding, setIsOnboarding])

  return (
    <>
      <Helmet title={pageTitle} />
      <Header>
        <StatusCheckBreadcrumbs
          endLabel={pageTitle}
          referrer={referrer}
          service={service}
        />
      </Header>
      <PageSection
        errorMessage={getMessageFromResponseOrError(
          statusCheckError ?? awsIAMRoleMetadataError,
          `Unable to load data required to ${
            formattedStatusCheckClone ? 'clone' : 'create a new'
          } Health Check. Please try refreshing the page.`
        )}
        hasError={isError}
        isLoading={isLoading}
      >
        <PageContainer>
          <Stack sx={{ rowGap: 3, marginBottom: 3 }}>
            <Typography data-test-create-status-check-title variant="h5">
              {headerText}
            </Typography>
            <Typography
              data-test-create-status-check-description
              sx={(muiTheme) => ({ maxWidth: muiTheme.breakpoints.values.sm })}
            >
              {CREATE_STATUS_CHECK_HEADER_DESCRIPTION}
            </Typography>
          </Stack>
          {formattedStatusCheckClone ? (
            <EditStatusCheckFormContainer
              serviceName={service?.name}
              statusCheck={formattedStatusCheckClone}
            />
          ) : (
            <CreateStatusCheckForm service={service} />
          )}
        </PageContainer>
      </PageSection>
    </>
  )
}
